"use client";
import { useEffect, useRef, useState } from "react";
import {
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Text,
  VStack,
  Input,
  Button,
  HStack,
} from "@chakra-ui/react";
import { signUp, emailOTP } from "queries";
import { useAppContext } from "hooks";
import ConsentModal from "./ConsentModal";

declare const google: any;

const GoogleForm = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const signInButtonRef = useRef(null);
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
  const { setJwt } = useAppContext();
  const [email, setEmail] = useState<string>("");
  const [getEmailOTPProcessing, setGetEmailOTPProcessing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");  // New state to handle error messages
  const [emailLoginStage, setEmailLoginStage] = useState<number>(0);
  const [otpSuccessMessage, setOtpSuccessMessage] = useState<string>("");
  const [consentModalIsOpen, setConsentModalIsOpen] = useState<boolean>(false);
  const [nsfwConsent, setNsfwConsent] = useState(true);
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [adultConsent, setAdultConsent] = useState(false);
  const [lastGoogleResponse, setLastGoogleResponse] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setGetEmailOTPProcessing(false);
      setEmailLoginStage(0);
      setOtpSuccessMessage("");
      setErrorMessage("");  // Clear error message when modal closes
    }
  }, [isOpen]);

  useEffect(() => {
    // Only attempt to sign up again if the modal was just closed and consents were possibly updated
    if (adultConsent) {
      retrySignup();
    }
  }, [adultConsent]);

  useEffect(() => {
    const renderButton = () => {
      if (signInButtonRef.current) {
        try {
          google.accounts.id.initialize({
            client_id: clientId,
            callback: handleCallbackResponse,
          });

          google.accounts.id.renderButton(signInButtonRef.current, {
            theme: "outline",
            size: "large",
          });
        } catch (error) {
          console.error("Error initializing Google Sign-In:", error);
        }
      }
    };

    if (isOpen && clientId) {
      setTimeout(() => {
        renderButton();
      }, 100);
    }
  }, [isOpen, clientId]);

  const retrySignup = () => {
    if (lastGoogleResponse) {
      handleCallbackResponse(lastGoogleResponse);
    }
  };

  const handleCallbackResponse = (response: any) => {
    setLastGoogleResponse(response);

    signUp({
      id_token: response.credential,
      provider: "google",
      nsfw_consent: false, //nsfwConsent,
      newsletter_consent: newsletterConsent,
      adult_consent: adultConsent,
    })
      .then((data) => {
        setJwt(data.encoded_jwt);
        onClose();
        setConsentModalIsOpen(false);
      })
      .catch((error: any) => {
        console.log(error.detail);
        if (error?.detail === "ConsentRequired") {
          setConsentModalIsOpen(true);
          onClose();
          console.log("I set the modal to open");
        } else if (error?.detail === "User is blocked and cannot log in") {
          setErrorMessage("Your account has been banned.");  // Set the error message
        }
        console.error("Sign up failed", error);
      });
  };

  const handleGetEmailOTP = () => {
    if (!email) return;

    setGetEmailOTPProcessing(true);

    emailOTP({ email: email })
      .then((data: any) => {
        setOtpSuccessMessage(
          String(
            data.message
              ? typeof data.message === "object"
                ? JSON.stringify(data.message)
                : data.message
              : data
          )
        );

        setEmailLoginStage(1);
        console.log("Get email otp success", data);
      })
      .catch((error: any) => {
        if (error?.detail === "User is blocked and cannot log in") {
          setErrorMessage("Your account has been banned.");  // Set the error message for blocked user
        } else {
          setErrorMessage(
            String(
              error.detail
                ? typeof error.detail === "object"
                  ? JSON.stringify(error.detail)
                  : error.detail
                : error
            )
          );  // Set other error messages
        }

        console.error("Get email otp failed", error);
      })
      .finally(() => setGetEmailOTPProcessing(false));
  };

  if (consentModalIsOpen) {
    return (
      <ConsentModal
        isOpen={consentModalIsOpen}
        onConsentChange={{ setNsfwConsent, setNewsletterConsent, setAdultConsent }}
        onClose={() => setConsentModalIsOpen(false)}
      />
    );
  }

  return (
    <Modal size="full" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent bg="rgba(10,14,29, 0.9)">
        <ModalCloseButton />
        <ModalBody>
          <Flex mt="200px" h="110px" justify="center" direction="column" align="center">
            <Text
              style={{
                background: "linear-gradient(to right,  #FFB5FF , #1AA5D3 )",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                MozBackgroundClip: "text",
              }}
              fontSize={{ base: "28px", lg: "58px" }}
              fontWeight="semibold"
              textAlign="center"
            >
              Log in or sign up in seconds
            </Text>
            
            {/* Error Message Display */}
            {errorMessage && (
              <Text color="red" fontWeight="bold" mt="20px">
                {errorMessage}
              </Text>
            )}

            {emailLoginStage == 0 && (
              <>
                <Flex mt="30px" ref={signInButtonRef} className="SignInDiv"></Flex>
                <Text mt="30px" mb="30px">
                  Or connect with e-mail
                </Text>
                <HStack
                  spacing="2"
                  align="stretch"
                  justifyContent="center"
                  alignItems="center"
                  w="100vw"
                  pl="10px"
                  pr="10px"
                >
                  <Input
                    placeholder="Type your email..."
                    h="60px"
                    maxW="300px"
                    variant="filled"
                    color="#8E8E8E"
                    bg="#1A1D21"
                    borderRadius="8px"
                    _hover={{ background: "#1A1D21" }}
                    _focus={{ background: "#1A1D21" }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button
                    h="50px"
                    maxW="200px"
                    color="white"
                    borderRadius="8px"
                    bg="#FF1B6D"
                    onClick={handleGetEmailOTP}
                    isDisabled={getEmailOTPProcessing}
                  >
                    <Text mr={{ base: "25px", md: "75px" }} ml={{ base: "25px", md: "75px" }}>
                      Send password
                    </Text>
                  </Button>
                </HStack>
              </>
            )}
            {emailLoginStage == 1 && (
              <>
                <VStack
                  mt="100px"
                  spacing="6"
                  align="stretch"
                  alignItems="center"
                  w="100vw"
                  pl="10px"
                  pr="10px"
                >
                  <Text maxW="500px" textAlign="center" mb="15px">
                    {otpSuccessMessage}
                  </Text>
                </VStack>
              </>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default GoogleForm;
